import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { tr, SUPPORTED_LANGUAGES } from "../lib/i18n";

const STORAGE_KEY = "tasklio_lang";
const DEFAULT_LANG = "en";

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (key, ...args) => tr(key, DEFAULT_LANG, ...args),
});

const readInitial = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;
  } catch {
    // private mode etc. — fall through to default.
  }
  return DEFAULT_LANG;
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(readInitial);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
      // Reflect on <html lang="..."> for accessibility / browser tooling.
      if (typeof document !== "undefined" && document.documentElement) {
        document.documentElement.setAttribute("lang", lang);
      }
    } catch {
      // ignore
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!SUPPORTED_LANGUAGES.includes(next)) return;
    setLangState(next);
  }, []);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      t: (key, ...args) => tr(key, lang, ...args),
    }),
    [lang, setLang]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useT = () => useContext(LanguageContext);
